// Pure rules behind the status tabs and the right-hand filter panel on
// /seller/orders (design docs/specs/2026-10-05-seller-orders-status-tabs-filter-design.md).
//
// Like sellerPackingRound.js next door, this is a module and not computed
// properties because this repo's jest cannot mount a .vue file
// (reference-bo-fn-jest-infra). Orders.vue only renders what lives here; the
// behaviour is specified in tests/unit/sellerOrderFilters.spec.js.
//
// --------------------------------------------------------------------------
// WHAT THIS FILE OWNS
//
// 1. The tab -> order_status map (spec §1). It MIRRORS the BE constant
//    `sellerOrderTabStatuses` in mdf-bo-api/controllers/Seller_orders.go
//    (spec §3.2). The BE is authoritative for filtering — this copy only
//    decides which sub-status radios the panel offers per tab and how a status
//    badge is coloured. Change both together.
//
// 2. The filter model <-> URL query (spec §5.4). Junk in the URL is dropped,
//    never thrown (spec §1.1 edge case 3): an unknown tab falls back to
//    `to_ship`, unknown channels / pay states disappear.
//
// 3. The active-filter chips (spec §5.1 item 2): one chip per field in use,
//    removable one at a time.
//
// 4. The tab counter text: 5,478 / 52.3K (spec §5.1 item 1).
// --------------------------------------------------------------------------

export const TAB_KEYS = ['all', 'to_ship', 'shipping', 'completed', 'cancelled', 'returns'];

/** Opening tab (spec §1, owner Q1): today's work, like TikTok Seller Center. */
export const DEFAULT_TAB = 'to_ship';

/**
 * Mirror of `sellerOrderTabStatuses` (mdf-bo-api/controllers/Seller_orders.go).
 * `all` filters nothing. 100 (awaiting payment) and unknown codes belong to no
 * tab but `all`.
 */
export const TAB_STATUSES = {
  all: [],
  to_ship: [111, 112, 115],
  shipping: [114, 121],
  completed: [122, 130],
  cancelled: [140],
  returns: [143, 141, 142]
};

/** Every code the `all` tab offers as a sub-status radio (spec §2 item 10). */
export const ALL_STATUS_CODES = [100, 111, 112, 115, 114, 121, 122, 130, 140, 143, 141, 142];

export const CHANNEL_OPTIONS = [
  { value: 'shop', labelKey: 'seller_orders.chip_web' },
  { value: 'member', labelKey: 'seller_orders.chip_member' },
  { value: 'app', labelKey: 'seller_orders.chip_app' },
  { value: 'chat', labelKey: 'seller_orders.chip_chat' }
];

export const DATE_TYPES = [
  { value: 'created', labelKey: 'seller_orders.date_created' },
  { value: 'paid', labelKey: 'seller_orders.date_paid' },
  { value: 'shipping', labelKey: 'seller_orders.date_shipping' }
];
export const DEFAULT_DATE_TYPE = 'created';

export const PAY_STATES = [
  { value: 'paid', labelKey: 'seller_orders.pay_state_paid' },
  { value: 'unpaid', labelKey: 'seller_orders.pay_state_unpaid' },
  { value: 'cod', labelKey: 'seller_orders.pay_state_cod' }
];

export const PACKING_STATES = [
  { value: 'none', labelKey: 'seller_orders.packing_none' },
  { value: 'in', labelKey: 'seller_orders.packing_in' }
];

/** Free-text fields are trimmed and cut here, the same limit the BE applies (spec §2). */
export const MAX_TEXT_LEN = 100;

const TEXT_FIELDS = ['order_q', 'customer_q', 'product_q'];
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const STATUS_RE = /^\d{1,4}$/;

const has = (obj, k) => Object.prototype.hasOwnProperty.call(obj, k);
const values = list => list.map(o => o.value);

function toCount(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

function cleanText(value) {
  if (value === undefined || value === null) return '';
  return String(Array.isArray(value) ? value[0] : value)
    .trim()
    .slice(0, MAX_TEXT_LEN);
}

function cleanDate(value) {
  const s = cleanText(value);
  return DATE_RE.test(s) ? s : '';
}

/** A CSV string or an array -> trimmed, de-duplicated, non-empty strings. */
function csvList(value) {
  if (value === undefined || value === null || value === '') return [];
  const raw = Array.isArray(value) ? value : String(value).split(',');
  const out = [];
  raw.forEach(v => {
    const s = String(v === undefined || v === null ? '' : v).trim();
    if (s && out.indexOf(s) < 0) out.push(s);
  });
  return out;
}

function oneOf(value, allowed, fallback) {
  const s = cleanText(value);
  return allowed.indexOf(s) >= 0 ? s : fallback;
}

export function isTabKey(key) {
  return TAB_KEYS.indexOf(key) >= 0;
}

/** The tab a status code lives in, '' when only `all` shows it (100, unknown). */
export function tabOfStatus(code) {
  const n = Number(code);
  const hit = TAB_KEYS.find(k => k !== 'all' && TAB_STATUSES[k].indexOf(n) >= 0);
  return hit || '';
}

/** The sub-status radios the panel offers on a tab (spec §5.2). */
export function subStatusOptions(tab) {
  if (tab === 'all' || !isTabKey(tab)) return ALL_STATUS_CODES.slice();
  return TAB_STATUSES[tab].slice();
}

const STATUS_BADGE = {
  to_ship: 'badge-warning',
  shipping: 'badge-info',
  completed: 'badge-success',
  cancelled: 'badge-danger',
  returns: 'badge-dark'
};

/** The สถานะ badge colour, by the tab the code belongs to (spec §5.1 item 4). */
export function statusBadgeClass(code) {
  return STATUS_BADGE[tabOfStatus(code)] || 'badge-secondary';
}

export function emptyFilters() {
  return {
    status: '',
    date_type: DEFAULT_DATE_TYPE,
    date_from: '',
    date_to: '',
    order_q: '',
    customer_q: '',
    product_q: '',
    channels: [],
    payment_method_type: 0,
    pay_state: '',
    shipping_providers: [],
    packing: '',
    wms_unmapped: false
  };
}

/** Deep-enough copy for the panel's draft (arrays are the only nested values). */
export function cloneFilters(filters) {
  const src = Object.assign(emptyFilters(), filters || {});
  return Object.assign({}, src, {
    channels: (src.channels || []).slice(),
    shipping_providers: (src.shipping_providers || []).slice()
  });
}

/**
 * normalizeFilters — any loose object (draft, URL, older state) -> the model.
 * Unknown values are dropped silently (spec §1.1 edge case 3).
 */
export function normalizeFilters(input) {
  const src = input || {};
  const f = emptyFilters();
  const status = cleanText(src.status);
  f.status = STATUS_RE.test(status) ? String(Number(status)) : '';
  f.date_from = cleanDate(src.date_from);
  f.date_to = cleanDate(src.date_to);
  f.date_type = oneOf(src.date_type, values(DATE_TYPES), DEFAULT_DATE_TYPE);
  TEXT_FIELDS.forEach(k => {
    f[k] = cleanText(src[k]);
  });
  const channelValues = values(CHANNEL_OPTIONS);
  f.channels = csvList(src.channels).filter(c => channelValues.indexOf(c) >= 0);
  f.payment_method_type = toCount(src.payment_method_type);
  f.pay_state = oneOf(src.pay_state, values(PAY_STATES), '');
  f.shipping_providers = csvList(src.shipping_providers).map(s => s.slice(0, MAX_TEXT_LEN));
  f.packing = oneOf(src.packing, values(PACKING_STATES), '');
  const w = src.wms_unmapped;
  f.wms_unmapped = w === true || w === 1 || w === '1' || w === 'true';
  return f;
}

/** `$route.query` -> { tab, filters, page }. */
export function filtersFromQuery(routeQuery) {
  const q = routeQuery || {};
  const tab = isTabKey(cleanText(q.tab)) ? cleanText(q.tab) : DEFAULT_TAB;
  const page = Math.max(1, toCount(cleanText(q.page)) || 1);
  return { tab, filters: normalizeFilters(q), page };
}

/**
 * The field values that are actually in use, flattened for a query string:
 * defaults and blanks are left out, arrays become CSV. Shared by the URL and
 * the API params so the two can never disagree about what is filtered.
 */
function activeParams(filters) {
  const f = normalizeFilters(filters);
  const out = {};
  if (f.status) out.status = f.status;
  if (f.date_from || f.date_to) {
    if (f.date_type !== DEFAULT_DATE_TYPE) out.date_type = f.date_type;
    if (f.date_from) out.date_from = f.date_from;
    if (f.date_to) out.date_to = f.date_to;
  }
  TEXT_FIELDS.forEach(k => {
    if (f[k]) out[k] = f[k];
  });
  if (f.channels.length) out.channels = f.channels.join(',');
  if (f.payment_method_type) out.payment_method_type = String(f.payment_method_type);
  if (f.pay_state) out.pay_state = f.pay_state;
  if (f.shipping_providers.length) out.shipping_providers = f.shipping_providers.join(',');
  if (f.packing) out.packing = f.packing;
  if (f.wms_unmapped) out.wms_unmapped = '1';
  return out;
}

/** { tab, filters, page } -> `$route.query` (string values, defaults omitted). */
export function queryFromFilters(tab, filters, page) {
  const out = {};
  const t = isTabKey(tab) ? tab : DEFAULT_TAB;
  if (t !== DEFAULT_TAB) out.tab = t;
  Object.assign(out, activeParams(filters));
  const p = toCount(page);
  if (p > 1) out.page = String(p);
  return out;
}

/**
 * apiParams — GET /api/v1/bo/seller/orders params (spec §3.1).
 *
 * `previewParams` is `previewQueryParams(...)` from sellerPackingRound.js: it
 * carries `payment_method_type`, the SAME param the list filter uses (spec §2
 * item 6), so it is merged last and the filter's value is passed into it by the
 * caller. `tab` is always sent — without it the BE skips tab_counts.
 */
export function apiParams(tab, filters, page, limit, previewParams, opts) {
  const out = {
    tab: isTabKey(tab) ? tab : DEFAULT_TAB,
    page: Math.max(1, toCount(page) || 1),
    limit: toCount(limit) || 20
  };
  Object.assign(out, activeParams(filters));
  if (opts && opts.withFacets) out.with_facets = 1;
  return Object.assign(out, previewParams || {});
}

function translate(labels, key) {
  const t = labels && typeof labels.t === 'function' ? labels.t : null;
  if (!t) return key;
  const s = t(key);
  return s === undefined || s === null ? key : String(s);
}

function statusText(labels, code) {
  const key = `seller_orders.status_${code}`;
  const s = translate(labels, key);
  return s === key ? String(code) : s;
}

function optionText(labels, list, value) {
  const hit = list.find(o => o.value === value);
  return hit ? translate(labels, hit.labelKey) : String(value);
}

/**
 * activeChips — one chip per field in use, in panel order (spec §5.1 item 2).
 *
 * `labels` = { t(key) -> string, paymentType(id) -> string }; both optional so
 * the function stays testable without i18n. The view renders
 * `$t(chip.labelKey) + ': ' + chip.text`.
 */
export function activeChips(filters, labels) {
  const f = normalizeFilters(filters);
  const chips = [];
  const push = (key, labelKey, text) => chips.push({ key, labelKey, text: String(text) });
  if (f.status) push('status', 'seller_orders.f_sub_status', statusText(labels, f.status));
  if (f.date_from || f.date_to) {
    const kind = optionText(labels, DATE_TYPES, f.date_type);
    push('date', 'seller_orders.f_date_type', `${kind} ${f.date_from || '…'} – ${f.date_to || '…'}`);
  }
  if (f.order_q) push('order_q', 'seller_orders.f_order_q', f.order_q);
  if (f.customer_q) push('customer_q', 'seller_orders.f_customer_q', f.customer_q);
  if (f.product_q) push('product_q', 'seller_orders.f_product_q', f.product_q);
  if (f.channels.length) {
    push('channels', 'seller_orders.f_channels', f.channels.map(c => optionText(labels, CHANNEL_OPTIONS, c)).join(', '));
  }
  if (f.payment_method_type) {
    const name = labels && typeof labels.paymentType === 'function' ? labels.paymentType(f.payment_method_type) : '';
    push('payment_method_type', 'seller_orders.filter_payment_type', name && name !== '-' ? name : f.payment_method_type);
  }
  if (f.pay_state) push('pay_state', 'seller_orders.f_pay_state', optionText(labels, PAY_STATES, f.pay_state));
  if (f.shipping_providers.length) push('shipping_providers', 'seller_orders.f_shipping', f.shipping_providers.join(', '));
  if (f.packing) push('packing', 'seller_orders.f_packing', optionText(labels, PACKING_STATES, f.packing));
  if (f.wms_unmapped) push('wms_unmapped', 'seller_orders.f_wms_unmapped', '✓');
  return chips;
}

/**
 * periodChip — the leading, non-closable "ช่วงเวลา: ทั้งหมด" tag.
 *
 * With no date range the list AND the ready-to-cut counts are all-time, and
 * before this tag nothing on the page said so. When a range is set the
 * closable `date` chip from activeChips() already names the window, so this
 * returns null. It is informational only: it is not a filter, so it never
 * counts toward activeCount() and has no ✕.
 */
export function periodChip(filters) {
  const f = normalizeFilters(filters);
  if (f.date_from || f.date_to) return null;
  return { labelKey: 'seller_orders.period_all' };
}

/** The n in "ตัวกรอง (n)". */
export function activeCount(filters) {
  return activeChips(filters).length;
}

/** Filters with one chip's field reset to its default. Never mutates the input. */
export function removeChip(filters, key) {
  const f = cloneFilters(normalizeFilters(filters));
  const empty = emptyFilters();
  if (key === 'date') {
    f.date_type = empty.date_type;
    f.date_from = '';
    f.date_to = '';
  } else if (has(empty, key)) {
    f[key] = Array.isArray(empty[key]) ? [] : empty[key];
  }
  return f;
}

/** `tab_counts` -> null when absent (old BE / failed count), else every key a count. */
export function normalizeTabCounts(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out = {};
  TAB_KEYS.concat(['unpaid', 'other']).forEach(k => {
    out[k] = toCount(raw[k]);
  });
  return out;
}

/** `status_counts` -> null or { "111": n, … } with junk dropped. */
export function normalizeStatusCounts(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out = {};
  Object.keys(raw).forEach(k => {
    if (STATUS_RE.test(k)) out[String(Number(k))] = toCount(raw[k]);
  });
  return out;
}

/** 5478 -> "5,478" · 52300 -> "52.3K" · 1250000 -> "1.3M". '' for null. */
export function formatTabCount(n) {
  if (n === null || n === undefined || n === '') return '';
  const v = toCount(n);
  const short = (x, unit) => `${x.toFixed(1).replace(/\.0$/, '')}${unit}`;
  if (v >= 1000000) return short(v / 1000000, 'M');
  if (v >= 10000) return short(v / 1000, 'K');
  return v.toLocaleString('en-US');
}

export default {
  TAB_KEYS,
  DEFAULT_TAB,
  TAB_STATUSES,
  ALL_STATUS_CODES,
  CHANNEL_OPTIONS,
  DATE_TYPES,
  DEFAULT_DATE_TYPE,
  PAY_STATES,
  PACKING_STATES,
  MAX_TEXT_LEN,
  isTabKey,
  tabOfStatus,
  subStatusOptions,
  statusBadgeClass,
  emptyFilters,
  cloneFilters,
  normalizeFilters,
  filtersFromQuery,
  queryFromFilters,
  apiParams,
  activeChips,
  activeCount,
  periodChip,
  removeChip,
  normalizeTabCounts,
  normalizeStatusCounts,
  formatTabCount
};
